export type UsageMeterTone = 'secondary' | 'warning' | 'destructive';

export interface UsageMeterProps {
  /** Emails reviewed this month. */
  used: number;
  /** Monthly quota. null = unlimited (Family tier — renders as info card). */
  quota: number | null;
  /**
   * Human-formatted reset date (e.g. "September 19"). The household's
   * subscription anniversary, not the start of the calendar month —
   * and formatted from the very instant the approval gate enforces.
   * Omit when the boundary isn't known; the line is then dropped
   * rather than guessed at.
   */
  resetLabel?: string;
  /**
   * Status copy shown beneath the bar at warning/limit. Omit at
   * default (<75%) and on the unlimited card.
   */
  statusLine?: string;
}

/**
 * Maps usage percentage to a tone band. The thresholds are part of
 * the spec: <75% Secondary, 75–99% Warning, ≥100% Destructive.
 */
function usageTone(used: number, quota: number): UsageMeterTone {
  const pct = quota === 0 ? 0 : (used / quota) * 100;
  if (pct >= 100) return 'destructive';
  if (pct >= 75) return 'warning';
  return 'secondary';
}

// `destructive` is the semantic tone name we expose; the classes below are
// the tokens that actually exist. `bg-destructive` / `text-destructive`
// have no matching `@theme` entry under Tailwind v4 and silently generate
// no CSS at all — that's why the bar wasn't turning red at the quota.
//
// The error tone is `text-error` (#a82222), NOT `danger` (#c94040). Both
// are red, but `danger` is the mid-tone fill and maps to no Figma DS
// variable; Figma's `text/error/default` and `icon/error/default` are the
// darker #a82222, and painting error states with `danger` is exactly what
// QA filed here (YAR-230, with a DevTools capture of `bg-danger` reading
// #C94040) and on YAR-154 / YAR-161 / YAR-284. `bg-text-error` reads oddly
// as a class name, but it is the `--color-text-error` token applied as a
// fill — the bar and the copy under it must be the same red.
const TONE_BAR: Record<UsageMeterTone, string> = {
  secondary: 'bg-foreground',
  warning: 'bg-warning',
  destructive: 'bg-text-error',
};

const TONE_TEXT: Record<UsageMeterTone, string> = {
  secondary: 'text-muted-foreground',
  warning: 'text-warning',
  destructive: 'text-text-error',
};

export function UsageMeter({
  used,
  quota,
  resetLabel,
  statusLine,
}: UsageMeterProps) {
  // Family / unlimited: no progress bar, no color states. Same
  // container, just an informational count.
  if (quota === null) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-border bg-background p-4">
        <div className="flex items-center justify-between text-sm font-medium text-text-heading">
          <span>Emails confirmed this month</span>
          <span>{used}</span>
        </div>
        {resetLabel && (
          <p className="text-sm text-muted-foreground">Resets {resetLabel}</p>
        )}
      </div>
    );
  }

  const tone = usageTone(used, quota);
  const pct = Math.min(
    100,
    Math.max(0, quota === 0 ? 0 : (used / quota) * 100),
  );

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-background p-4">
      <div className="flex items-center justify-between text-sm font-medium text-text-heading">
        <span>Emails confirmed this month</span>
        <span>
          {used} of {quota}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label="Emails confirmed this month"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={quota}
        className="relative h-1 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className={`h-full ${TONE_BAR[tone]} transition-[width] duration-300 ease-out`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {statusLine && tone !== 'secondary' && (
        <p className={`text-sm ${TONE_TEXT[tone]}`}>{statusLine}</p>
      )}
      {resetLabel && (
        <p className="text-sm text-muted-foreground">Resets {resetLabel}</p>
      )}
    </div>
  );
}
